import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Send, HelpCircle, Wrench } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

const generalSchema = z.object({
  subjectType: z.string().min(1, 'Välj ärendetyp'),
  name: z.string().trim().min(1, 'Namn krävs').max(100),
  email: z.string().trim().email('Ogiltig e-postadress').max(255),
  phone: z.string().max(30).optional(),
  company: z.string().max(100).optional(),
  message: z.string().trim().min(1, 'Meddelande krävs').max(2000),
  honeypot: z.string().max(0),
});

const supportSchema = z.object({
  subjectType: z.string().min(1, 'Välj ärendetyp'),
  name: z.string().trim().min(1, 'Namn krävs').max(100),
  email: z.string().trim().email('Ogiltig e-postadress').max(255),
  phone: z.string().max(30).optional(),
  company: z.string().max(100).optional(),
  product: z.string().max(200).optional(),
  message: z.string().trim().min(1, 'Meddelande krävs').max(2000),
  honeypot: z.string().max(0),
});

type GeneralFormData = z.infer<typeof generalSchema>;
type SupportFormData = z.infer<typeof supportSchema>;

function GeneralForm() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mountedAt] = useState(() => Date.now());

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<GeneralFormData>({
    resolver: zodResolver(generalSchema),
    defaultValues: { subjectType: '', name: '', email: '', phone: '', company: '', message: '', honeypot: '' },
  });

  const onSubmit = async (data: GeneralFormData) => {
    if (data.honeypot) return;
    setIsSubmitting(true);
    try {
      const { error, data: responseData } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact_general',
          data: {
            subjectType: data.subjectType,
            name: data.name,
            email: data.email,
            phone: data.phone || '',
            company: data.company || '',
            message: data.message,
            honeypot: data.honeypot,
            submitted_at: mountedAt,
            lang,
          },
        },
      });
      if (error) {
        if (typeof error === 'object' && 'context' in error && (error as any).context?.status === 429) {
          toast({ title: t('contact.rateLimitTitle'), description: t('contact.rateLimitMessage'), variant: 'destructive' });
          return;
        }
        throw error;
      }
      toast({ title: t('contact.successTitle'), description: t('contact.successMessage') });
      reset();
    } catch (err) {
      console.error('Contact email error:', err);
      toast({ title: t('contact.errorTitle'), description: t('contact.errorMessage'), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="hidden" aria-hidden="true">
        <Input {...register('honeypot')} tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="general-subjectType">{t('contact.subjectType')} *</Label>
        <Select onValueChange={(value) => setValue('subjectType', value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('contact.selectSubject')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="invoice">{t('contact.subjectInvoice')}</SelectItem>
            <SelectItem value="quote">{t('contact.subjectQuote')}</SelectItem>
            <SelectItem value="other">{t('contact.subjectOther')}</SelectItem>
          </SelectContent>
        </Select>
        {errors.subjectType && <p className="text-sm text-destructive">{errors.subjectType.message}</p>}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="general-name">{t('contact.name')} *</Label>
          <Input id="general-name" {...register('name')} placeholder={t('contact.namePlaceholder')} />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="general-email">{t('contact.email')} *</Label>
          <Input id="general-email" type="email" {...register('email')} placeholder={t('contact.emailPlaceholder')} />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="general-phone">{t('contact.phone')}</Label>
          <Input id="general-phone" type="tel" {...register('phone')} placeholder={t('contact.phonePlaceholder')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="general-company">{t('contact.company')}</Label>
          <Input id="general-company" {...register('company')} placeholder={t('contact.companyPlaceholder')} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="general-message">{t('contact.message')} *</Label>
        <Textarea id="general-message" {...register('message')} placeholder={t('contact.messagePlaceholder')} rows={5} />
        {errors.message && <p className="text-sm text-destructive">{errors.message.message}</p>}
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
        {isSubmitting ? t('contact.sending') : <><Send className="w-4 h-4 mr-2" />{t('contact.send')}</>}
      </Button>
    </form>
  );
}

function SupportForm() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mountedAt] = useState(() => Date.now());

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SupportFormData>({
    resolver: zodResolver(supportSchema),
    defaultValues: { subjectType: '', name: '', email: '', phone: '', company: '', product: '', message: '', honeypot: '' },
  });

  const onSubmit = async (data: SupportFormData) => {
    if (data.honeypot) return;
    setIsSubmitting(true);
    try {
      const { error, data: responseData } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact_support',
          data: {
            subjectType: data.subjectType,
            name: data.name,
            email: data.email,
            phone: data.phone || '',
            company: data.company || '',
            product: data.product || '',
            message: data.message,
            honeypot: data.honeypot,
            submitted_at: mountedAt,
            lang,
          },
        },
      });
      if (error) {
        if (typeof error === 'object' && 'context' in error && (error as any).context?.status === 429) {
          toast({ title: t('contact.rateLimitTitle'), description: t('contact.rateLimitMessage'), variant: 'destructive' });
          return;
        }
        throw error;
      }
      toast({ title: t('contact.successTitle'), description: t('contact.successMessage') });
      reset();
    } catch (err) {
      console.error('Support email error:', err);
      toast({ title: t('contact.errorTitle'), description: t('contact.errorMessage'), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="hidden" aria-hidden="true">
        <Input {...register('honeypot')} tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="support-subjectType">{t('contact.subjectType')} *</Label>
        <Select onValueChange={(value) => setValue('subjectType', value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('contact.selectSubject')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="delivery">{t('contact.subjectDelivery')}</SelectItem>
            <SelectItem value="dataContent">{t('contact.subjectDataContent')}</SelectItem>
            <SelectItem value="fileFormat">{t('contact.subjectFileFormat')}</SelectItem>
            <SelectItem value="other">{t('contact.subjectOther')}</SelectItem>
          </SelectContent>
        </Select>
        {errors.subjectType && <p className="text-sm text-destructive">{errors.subjectType.message}</p>}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="support-name">{t('contact.name')} *</Label>
          <Input id="support-name" {...register('name')} placeholder={t('contact.namePlaceholder')} />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="support-email">{t('contact.email')} *</Label>
          <Input id="support-email" type="email" {...register('email')} placeholder={t('contact.emailPlaceholder')} />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="support-phone">{t('contact.phone')}</Label>
          <Input id="support-phone" type="tel" {...register('phone')} placeholder={t('contact.phonePlaceholder')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="support-company">{t('contact.company')}</Label>
          <Input id="support-company" {...register('company')} placeholder={t('contact.companyPlaceholder')} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="support-product">{t('contact.product')}</Label>
        <Input id="support-product" {...register('product')} placeholder={t('contact.productPlaceholder')} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="support-message">{t('contact.message')} *</Label>
        <Textarea id="support-message" {...register('message')} placeholder={t('contact.messagePlaceholder')} rows={5} />
        {errors.message && <p className="text-sm text-destructive">{errors.message.message}</p>}
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
        {isSubmitting ? t('contact.sending') : <><Send className="w-4 h-4 mr-2" />{t('contact.send')}</>}
      </Button>
    </form>
  );
}

export function ContactForm() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
            activeTab === 'general'
              ? 'border-primary bg-primary/5 shadow-sm'
              : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
          }`}
        >
          <div className={`mt-0.5 rounded-md p-2 ${activeTab === 'general' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <HelpCircle className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-sm">{t('contact.tabGeneral')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('contact.tabGeneralDesc')}</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('support')}
          className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
            activeTab === 'support'
              ? 'border-primary bg-primary/5 shadow-sm'
              : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
          }`}
        >
          <div className={`mt-0.5 rounded-md p-2 ${activeTab === 'support' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <Wrench className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-sm">{t('contact.tabSupport')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('contact.tabSupportDesc')}</p>
          </div>
        </button>
      </div>
      <TabsContent value="general">
        <GeneralForm />
      </TabsContent>
      <TabsContent value="support">
        <SupportForm />
      </TabsContent>
    </Tabs>
  );
}
